// src/pages/buyers/BuyerOrderManagement.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  LinearProgress,
  Fade,
  Zoom,
  CardActions,
  CircularProgress,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
  Popover,
  AlertTitle,
  Badge,
  Stack,
  Paper as MuiPaper,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Autocomplete,
  Collapse,
  IconButton as MuiIconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Dashboard as DashboardIcon,
  ShoppingCart as ShoppingCartIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  ShoppingBag as ShoppingBagIcon,
  Star as StarIcon,
  StarHalf as StarHalfIcon,
  StarOutline as StarOutlineIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Store as StoreIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  HourglassEmpty as HourglassEmptyIcon,
  LocalOffer as LocalOfferIcon,
  Place as PlaceIcon,
  PersonOutline as PersonOutlineIcon,
  CalendarToday as CalendarTodayIcon,
  Receipt as ReceiptIcon,
  Send as SendIcon,
  Menu as MenuIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  Assignment as AssignmentIcon,
  Message as MessageIcon,
  TrackChanges as TrackChangesIcon,
  Timeline as TimelineIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { BRAND_COLORS, StatusChip } from './components/styles';
import { formatCurrency, formatDate } from './components/utils';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================

const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  whatsapp: '#25D366',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// ============================================
// API CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  ORDERS: {
    LIST: `${API_BASE_URL}/orders/orders_.php`,
    UPDATE: (id) => `${API_BASE_URL}/orders/orders_.php?id=${id}`,
  },
  BUYER: {
    PROFILE: `${API_BASE_URL}/buyers/profile.php`,
  },
};

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[BUYER-ORDERS] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[BUYER-ORDERS] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[BUYER-ORDERS] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[BUYER-ORDERS] ⚠️ ${message}`, data || ''),
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  return `${base}${imagePath}`;
};

const getStatusColor = (status) => {
  const colors = {
    'pending': COLORS.warning,
    'processing': COLORS.info,
    'shipped': '#6A1B9A',
    'delivered': COLORS.success,
    'cancelled': COLORS.error,
    'paid': COLORS.success,
    'failed': COLORS.error,
  };
  return colors[status] || COLORS.grey[600];
};

const getStatusLabel = (status) => {
  const labels = {
    'pending': 'Pending',
    'processing': 'Processing',
    'shipped': 'Shipped',
    'delivered': 'Delivered',
    'cancelled': 'Cancelled',
    'paid': 'Paid',
    'failed': 'Failed',
  };
  return labels[status] || status || 'N/A';
};

const getStatusIcon = (status) => {
  const icons = {
    'pending': <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
    'processing': <CircularProgress size={16} />,
    'shipped': <LocalShippingIcon sx={{ fontSize: 16 }} />,
    'delivered': <CheckCircleIcon sx={{ fontSize: 16 }} />,
    'cancelled': <CancelIcon sx={{ fontSize: 16 }} />,
  };
  return icons[status] || <ScheduleIcon sx={{ fontSize: 16 }} />;
};

const getStatusStep = (status) => {
  const steps = {
    'pending': 0,
    'processing': 1,
    'shipped': 2,
    'delivered': 3,
    'cancelled': -1,
  };
  return steps[status] || 0;
};

const getPaymentStatusColor = (status) => {
  const colors = {
    'paid': COLORS.success,
    'pending': COLORS.warning,
    'failed': COLORS.error,
    'refunded': '#795548',
  };
  return colors[status] || COLORS.grey[600];
};

const getPaymentStatusLabel = (status) => {
  const labels = {
    'paid': 'Paid',
    'pending': 'Pending',
    'failed': 'Failed',
    'refunded': 'Refunded',
  };
  return labels[status] || status || 'N/A';
};

const formatDateShort = (date) => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

const formatTime = (date) => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

// ============================================
// CUSTOM COMPONENTS
// ============================================

const OrderStatusBadge = ({ status, size = 'medium' }) => {
  const color = getStatusColor(status);
  const label = getStatusLabel(status);
  const icon = getStatusIcon(status);
  
  return (
    <Chip
      icon={icon}
      label={label}
      size={size}
      sx={{
        bgcolor: alpha(color, 0.12),
        color: color,
        fontWeight: 700,
        border: `1px solid ${alpha(color, 0.15)}`,
        '& .MuiChip-icon': {
          color: color,
        },
        ...(size === 'small' && {
          height: 26,
          '& .MuiChip-label': {
            fontSize: '0.7rem',
            px: 1.5,
            fontWeight: 700,
          },
        }),
      }}
    />
  );
};

const OrderTimeline = ({ order }) => {
  const statuses = ['pending', 'processing', 'shipped', 'delivered'];
  const currentStep = getStatusStep(order?.status);
  
  // Generate timeline events based on order data
  const events = [
    { label: 'Order Placed', status: 'pending', date: order?.order_date, icon: <ReceiptIcon /> },
    { label: 'Processing', status: 'processing', date: order?.order_date, icon: <SettingsIcon /> },
    { label: 'Shipped', status: 'shipped', date: order?.order_date, icon: <LocalShippingIcon /> },
    { label: 'Delivered', status: 'delivered', date: order?.order_date, icon: <CheckCircleIcon /> },
  ];

  return (
    <Box sx={{ py: 2 }}>
      <Stepper activeStep={currentStep} orientation="vertical" sx={{ '& .MuiStepLabel-label': { fontWeight: 600 } }}>
        {events.map((event, index) => {
          const isActive = index <= currentStep;
          const isLast = index === events.length - 1;
          const color = isActive ? getStatusColor(event.status) : COLORS.grey[300];
          
          return (
            <Step key={index} active={isActive}>
              <StepLabel
                StepIconComponent={() => (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: isActive ? alpha(color, 0.12) : COLORS.grey[100],
                      color: isActive ? color : COLORS.grey[400],
                      border: `2px solid ${isActive ? alpha(color, 0.3) : COLORS.grey[200]}`,
                    }}
                  >
                    {event.icon}
                  </Avatar>
                )}
                sx={{
                  '& .MuiStepLabel-label': {
                    color: isActive ? COLORS.grey[900] : COLORS.grey[500],
                    fontWeight: isActive ? 700 : 500,
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={isActive ? 700 : 500}>
                    {event.label}
                  </Typography>
                  <Typography variant="caption" color={isActive ? COLORS.grey[600] : COLORS.grey[400]}>
                    {formatDate(event.date)}
                  </Typography>
                </Box>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

const OrderCard = ({ order, onView, onCancel, onTrack }) => {
  const [expanded, setExpanded] = useState(false);
  const statusColor = getStatusColor(order?.status);
  const canCancel = order?.status === 'pending' || order?.status === 'processing';
  
  return (
    <Fade in>
      <Card
        sx={{
          mb: 2.5,
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${COLORS.grey[200]}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#FFFFFF',
          '&:hover': {
            boxShadow: '0 16px 48px rgba(0,0,0,0.08)',
            borderColor: COLORS.primary,
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            {/* Left section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(statusColor, 0.12),
                  color: statusColor,
                  width: 48,
                  height: 48,
                  border: `2px solid ${alpha(statusColor, 0.15)}`,
                }}
              >
                <ReceiptIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: COLORS.grey[900], fontSize: '1rem' }}>
                  Order #{order?.id || 'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 500 }}>
                    {formatDateShort(order?.order_date)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: COLORS.grey[400] }}>•</Typography>
                  <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 500 }}>
                    {order?.items || 0} items
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Right section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <OrderStatusBadge status={order?.status} />
              <Chip
                label={getPaymentStatusLabel(order?.payment_status)}
                size="small"
                sx={{
                  bgcolor: alpha(getPaymentStatusColor(order?.payment_status), 0.1),
                  color: getPaymentStatusColor(order?.payment_status),
                  fontWeight: 700,
                  height: 26,
                  border: `1px solid ${alpha(getPaymentStatusColor(order?.payment_status), 0.15)}`,
                  '& .MuiChip-label': { fontSize: '0.7rem', px: 1.5, fontWeight: 700 },
                }}
              />
              <IconButton
                size="small"
                onClick={() => onView(order)}
                sx={{ 
                  bgcolor: COLORS.grey[100],
                  '&:hover': { bgcolor: COLORS.grey[200] },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          {/* Order Details */}
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mt: 2.5, pt: 2.5, borderTop: `1px solid ${COLORS.grey[100]}` }}>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                Total Amount
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.primary, fontSize: '1.25rem', mt: 0.25 }}>
                {formatCurrency(order?.total_amount || 0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                Delivery Method
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25, textTransform: 'capitalize' }}>
                {order?.delivery_method || 'Standard'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                Tracking
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25 }}>
                {order?.tracking || 'N/A'}
              </Typography>
            </Box>
            {order?.intended_use && (
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                  Intended Use
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: COLORS.grey[700], mt: 0.25 }}>
                  {order.intended_use}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: `1px solid ${COLORS.grey[100]}` }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => setExpanded(!expanded)}
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: 600,
                  color: COLORS.grey[600],
                  '&:hover': { color: COLORS.primary },
                }}
              >
                {expanded ? 'Show Less' : 'Track Order'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canCancel && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => onCancel(order)}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Cancel Order
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => onView(order)}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                  boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
                  '&:hover': {
                    boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                View Details
              </Button>
            </Box>
          </Box>
          
          {/* Expandable */}
          <Collapse in={expanded} sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <OrderTimeline order={order} />
          </Collapse>
        </CardContent>
      </Card>
    </Fade>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const BuyerOrderManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'buyer';
  const accessToken = localStorage.getItem('access_token');

  // ============================================
  // STATE
  // ============================================

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('orders');
  const [buyerData, setBuyerData] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [viewMode, setViewMode] = useState('table');
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    totalRevenue: 0,
  });

  // ============================================
  // API FUNCTIONS
  // ============================================

  const getBuyerId = useCallback(async (userId) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BUYER.PROFILE}?user_id=${userId}`);
      const result = await response.json();
      if (result.success && result.data) {
        return result.data.buyer_id || result.data.id;
      }
      return null;
    } catch (e) {
      logger.error('Error getting buyer_id:', e);
      return null;
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    logger.info('Fetching orders...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      // Get the buyer_id dynamically
      let buyerId = null;
      
      if (buyerData && buyerData.buyer_id) {
        buyerId = buyerData.buyer_id;
      } else if (buyerData && buyerData.id && buyerData.id !== userId) {
        buyerId = buyerData.id;
      } else {
        buyerId = await getBuyerId(userId);
        if (buyerId) {
          // Update buyerData with the buyer_id
          setBuyerData(prev => ({ ...prev, buyer_id: buyerId }));
        }
      }

      if (!buyerId) {
        // If we still don't have a buyerId, try to get it from the profile
        const profileResponse = await fetch(`${API_ENDPOINTS.BUYER.PROFILE}?user_id=${userId}`);
        const profileResult = await profileResponse.json();
        if (profileResult.success && profileResult.data) {
          buyerId = profileResult.data.buyer_id || profileResult.data.id;
          setBuyerData(profileResult.data);
        }
      }

      if (!buyerId) {
        throw new Error('Could not determine buyer_id. Please ensure you have a buyer profile.');
      }

      const url = `${API_ENDPOINTS.ORDERS.LIST}?buyer_id=${buyerId}&user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        let ordersData = [];

        if (Array.isArray(result.data)) {
          ordersData = result.data;
        } else if (result.data && typeof result.data === 'object') {
          if (Array.isArray(result.data.data)) {
            ordersData = result.data.data;
          } else if (Array.isArray(result.data.orders)) {
            ordersData = result.data.orders;
          } else {
            const arrayKey = Object.keys(result.data).find((key) => Array.isArray(result.data[key]));
            if (arrayKey) {
              ordersData = result.data[arrayKey];
            }
          }
        } else if (Array.isArray(result)) {
          ordersData = result;
        }

        setOrders(ordersData);
        calculateStats(ordersData);
        logger.info(`✅ Loaded ${ordersData.length} orders`);
      } else {
        throw new Error(result.message || 'Failed to fetch orders');
      }
    } catch (error) {
      logger.error('Error fetching orders:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load orders. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [buyerData, getBuyerId]);

  const calculateStats = (ordersData) => {
    const statsData = {
      total: ordersData.length,
      pending: ordersData.filter(o => o.status === 'pending').length,
      processing: ordersData.filter(o => o.status === 'processing').length,
      shipped: ordersData.filter(o => o.status === 'shipped').length,
      delivered: ordersData.filter(o => o.status === 'delivered').length,
      cancelled: ordersData.filter(o => o.status === 'cancelled').length,
      totalRevenue: ordersData
        .filter(o => o.status === 'delivered')
        .reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0),
    };
    setStats(statsData);
  };

  const cancelOrder = useCallback(async (orderId, reason) => {
    if (!orderId) {
      setSnackbar({ open: true, message: 'Invalid order', severity: 'error' });
      return;
    }

    setSnackbar({ open: true, message: 'Cancelling order...', severity: 'info' });

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const url = `${API_ENDPOINTS.ORDERS.UPDATE(orderId)}&user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status: 'cancelled',
          user_id: userId,
          id: orderId,
          cancellation_reason: reason || 'Customer requested cancellation'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Update orders list
        const updatedOrders = orders.map(order => 
          order.id === orderId 
            ? { ...order, status: 'cancelled' } 
            : order
        );
        setOrders(updatedOrders);
        calculateStats(updatedOrders);
        
        // Close dialogs
        setOrderDetailsDialog(false);
        setCancelDialog(false);
        setCancelReason('');
        
        setSnackbar({ 
          open: true, 
          message: 'Order cancelled successfully', 
          severity: 'success' 
        });
        
        setLastRefreshed(new Date());
      } else {
        throw new Error(result.message || 'Failed to cancel order');
      }
    } catch (error) {
      logger.error('Error cancelling order:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to cancel order', 
        severity: 'error' 
      });
    }
  }, [orders]);

  // ============================================
  // FILTERING & SORTING
  // ============================================

  useEffect(() => {
    let result = [...orders];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(order => 
        (order.id && order.id.toString().includes(term)) ||
        (order.supplier_name && order.supplier_name.toLowerCase().includes(term)) ||
        (order.ManufacturerName && order.ManufacturerName.toLowerCase().includes(term)) ||
        (order.session_id && order.session_id.toLowerCase().includes(term))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }

    // Payment filter
    if (paymentFilter !== 'all') {
      result = result.filter(order => order.payment_status === paymentFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      result = result.filter(order => {
        const orderDate = new Date(order.order_date);
        switch (dateFilter) {
          case 'today':
            return orderDate >= today;
          case 'week': {
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return orderDate >= weekAgo;
          }
          case 'month': {
            const monthAgo = new Date(today);
            monthAgo.setDate(monthAgo.getDate() - 30);
            return orderDate >= monthAgo;
          }
          default:
            return true;
        }
      });
    }

    // Sorting
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.order_date) - new Date(a.order_date));
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.order_date) - new Date(b.order_date));
        break;
      case 'highest':
        result.sort((a, b) => parseFloat(b.total_amount) - parseFloat(a.total_amount));
        break;
      case 'lowest':
        result.sort((a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount));
        break;
      default:
        break;
    }

    setFilteredOrders(result);
  }, [orders, searchTerm, statusFilter, paymentFilter, dateFilter, sortBy]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || userType !== 'buyer') {
      navigate('/RawAuth');
      return;
    }
    fetchOrders();
  }, [fetchOrders, navigate, userType]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleTabChange = useCallback((tab) => {
  setSelectedTab(tab);
  navigate(tab);
}, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleChangePassword = useCallback(() => {
    setChangePasswordDialog(true);
    setProfileAnchorEl(null);
  }, []);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderDetailsDialog(true);
  };

  const handleCancelOrder = (order) => {
    setSelectedOrder(order);
    setCancelReason('');
    setCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    if (selectedOrder) {
      cancelOrder(selectedOrder.id, cancelReason);
    }
  };

  const handleRefresh = () => {
    fetchOrders();
    setSnackbar({ open: true, message: 'Orders refreshed', severity: 'info' });
  };

  const handleExport = () => {
    const headers = ['Order ID', 'Total Amount', 'Status', 'Payment Status', 'Order Date', 'Items'];
    const rows = filteredOrders.map(order => [
      order.id,
      order.total_amount || 0,
      getStatusLabel(order.status),
      getPaymentStatusLabel(order.payment_status),
      formatDate(order.order_date),
      order.items || 0,
    ]);
    
    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    setSnackbar({ open: true, message: 'Orders exported successfully', severity: 'success' });
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
// ============================================
// RENDER FUNCTIONS - Fix the renderStats function
// ============================================

const renderStats = () => {
  const statItems = [
    { 
      icon: <ShoppingBagIcon />, 
      title: 'Total Requsts', 
      value: stats.total,
      color: COLORS.primary,
    },
    { 
      icon: <HourglassEmptyIcon />, 
      title: 'Pending', 
      value: stats.pending,
      subtitle: `${stats.pending > 0 ? ((stats.pending / stats.total) * 100).toFixed(0) : 0}% of total`,
      color: COLORS.warning,  // Changed from COLORS.status.pending
    },
    { 
      icon: <LocalShippingIcon />, 
      title: 'Processing', 
      value: stats.processing,
      subtitle: `${stats.processing > 0 ? ((stats.processing / stats.total) * 100).toFixed(0) : 0}% of total`,
      color: COLORS.info,  // Changed from COLORS.status.processing
    },
    { 
      icon: <CheckCircleIcon />, 
      title: 'Delivered', 
      value: stats.delivered,
      subtitle: `${stats.delivered > 0 ? ((stats.delivered / stats.total) * 100).toFixed(0) : 0}% of total`,
      color: COLORS.success,  // Changed from COLORS.status.delivered
    },
    { 
      icon: <CancelIcon />, 
      title: 'Cancelled', 
      value: stats.cancelled,
      subtitle: `${stats.cancelled > 0 ? ((stats.cancelled / stats.total) * 100).toFixed(0) : 0}% of total`,
      color: COLORS.error,  // Changed from COLORS.status.cancelled
    },
    { 
      icon: <AttachMoneyIcon />, 
      title: 'Total Spent', 
      value: formatCurrency(stats.totalRevenue),
      subtitle: `From ${stats.delivered} delivered orders`,
      color: COLORS.accent,
    },
  ];

  return (
    <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
      {statItems.map((item, index) => (
        <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
          <Card
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${alpha(item.color, 0.08)} 0%, ${alpha(item.color, 0.02)} 100%)`,
              border: `1px solid ${alpha(item.color, 0.12)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 40px ${alpha(item.color, 0.15)}`,
                borderColor: alpha(item.color, 0.3),
              },
            }}
          >
            <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  mx: 'auto',
                  mb: 1,
                  bgcolor: alpha(item.color, 0.1),
                  color: item.color,
                }}
              >
                {item.icon}
              </Avatar>
              <Typography variant="h4" fontWeight={800} sx={{ color: item.color, fontSize: '1.5rem' }}>
                {item.value}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.grey[600], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                {item.title}
              </Typography>
              {item.subtitle && (
                <Typography variant="caption" sx={{ color: COLORS.grey[500], display: 'block', mt: 0.25, fontWeight: 500 }}>
                  {item.subtitle}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

  const renderFilters = () => (
    <Paper 
      sx={{ 
        p: 2.5, 
        mb: 3.5, 
        borderRadius: 4,
        border: `1px solid ${COLORS.grey[200]}`,
        background: '#FFFFFF',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'center',
      }}
    >
      <TextField
        placeholder="Search orders by ID, supplier..."
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: COLORS.grey[500] }} />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ 
          minWidth: 200, 
          flex: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover fieldset': {
              borderColor: COLORS.primary,
            },
          },
        }}
      />
      
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25 },
          }}
        >
          <MenuItem value="all" sx={{ fontWeight: 600 }}>All Statuses</MenuItem>
          <MenuItem value="pending" sx={{ fontWeight: 500 }}>Pending</MenuItem>
          <MenuItem value="processing" sx={{ fontWeight: 500 }}>Processing</MenuItem>
          <MenuItem value="shipped" sx={{ fontWeight: 500 }}>Shipped</MenuItem>
          <MenuItem value="delivered" sx={{ fontWeight: 500 }}>Delivered</MenuItem>
          <MenuItem value="cancelled" sx={{ fontWeight: 500 }}>Cancelled</MenuItem>
        </Select>
      </FormControl>
      
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25 },
          }}
        >
          <MenuItem value="all" sx={{ fontWeight: 600 }}>All Payments</MenuItem>
          <MenuItem value="paid" sx={{ fontWeight: 500 }}>Paid</MenuItem>
          <MenuItem value="pending" sx={{ fontWeight: 500 }}>Pending</MenuItem>
          <MenuItem value="failed" sx={{ fontWeight: 500 }}>Failed</MenuItem>
        </Select>
      </FormControl>
      
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25 },
          }}
        >
          <MenuItem value="all" sx={{ fontWeight: 600 }}>All Time</MenuItem>
          <MenuItem value="today" sx={{ fontWeight: 500 }}>Today</MenuItem>
          <MenuItem value="week" sx={{ fontWeight: 500 }}>This Week</MenuItem>
          <MenuItem value="month" sx={{ fontWeight: 500 }}>This Month</MenuItem>
        </Select>
      </FormControl>
      
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25 },
          }}
        >
          <MenuItem value="newest" sx={{ fontWeight: 600 }}>Newest First</MenuItem>
          <MenuItem value="oldest" sx={{ fontWeight: 500 }}>Oldest First</MenuItem>
          <MenuItem value="highest" sx={{ fontWeight: 500 }}>Highest Amount</MenuItem>
          <MenuItem value="lowest" sx={{ fontWeight: 500 }}>Lowest Amount</MenuItem>
        </Select>
      </FormControl>
      
      <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
        <Tooltip title="Refresh">
          <IconButton 
            onClick={handleRefresh} 
            size="small"
            sx={{ 
              bgcolor: COLORS.grey[100],
              borderRadius: 2,
              '&:hover': { bgcolor: COLORS.grey[200] },
            }}
          >
            <RefreshIcon sx={{ color: COLORS.grey[700] }} />
          </IconButton>
        </Tooltip>
      
      </Box>
    </Paper>
  );

  const renderTable = () => (
    <TableContainer 
      component={Paper} 
      sx={{ 
        borderRadius: 4,
        border: `1px solid ${COLORS.grey[200]}`,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
      }}
    >
      <Table>
        <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Order ID
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Supplier
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Amount
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Payment
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Date
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredOrders.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                <Typography color={COLORS.grey[500]} fontWeight={600}>No Request found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredOrders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => {
                const statusColor = getStatusColor(order.status);
                const canCancel = order.status === 'pending' || order.status === 'processing';
                return (
                  <TableRow 
                    key={order.id}
                    hover
                    sx={{ 
                      '&:hover': { bgcolor: alpha(COLORS.primary, 0.02) },
                      cursor: 'pointer',
                      transition: 'background 0.2s ease',
                    }}
                    onClick={() => handleViewOrder(order)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.grey[900] }}>
                        #{order.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                        {order.supplier_name || order.ManufacturerName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.primary }}>
                        {formatCurrency(order.total_amount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <OrderStatusBadge status={order.status} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPaymentStatusLabel(order.payment_status)}
                        size="small"
                        sx={{
                          bgcolor: alpha(getPaymentStatusColor(order.payment_status), 0.1),
                          color: getPaymentStatusColor(order.payment_status),
                          fontWeight: 700,
                          height: 24,
                          border: `1px solid ${alpha(getPaymentStatusColor(order.payment_status), 0.15)}`,
                          '& .MuiChip-label': { fontSize: '0.65rem', px: 1.5, fontWeight: 700 },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.grey[600] }}>
                        {formatDateShort(order.order_date)}
                        <br />
                        {formatTime(order.order_date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewOrder(order);
                            }}
                            sx={{ 
                              color: COLORS.primary,
                              bgcolor: alpha(COLORS.primary, 0.04),
                              '&:hover': { bgcolor: alpha(COLORS.primary, 0.12) },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canCancel && (
                          <Tooltip title="Cancel Order">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelOrder(order);
                              }}
                              sx={{ 
                                color: COLORS.error,
                                bgcolor: alpha(COLORS.error, 0.04),
                                '&:hover': { bgcolor: alpha(COLORS.error, 0.12) },
                              }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: `1px solid ${COLORS.grey[200]}`,
          '& .MuiTablePagination-select': { fontWeight: 600 },
        }}
      />
    </TableContainer>
  );

  const renderCardView = () => (
    <Box>
      {filteredOrders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ color: COLORS.grey[500], fontWeight: 600 }}>No Request found</Typography>
        </Box>
      ) : (
        filteredOrders
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onView={handleViewOrder}
              onCancel={handleCancelOrder}
              onTrack={handleViewOrder}
            />
          ))
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: `1px solid ${COLORS.grey[200]}`,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          mt: 2,
          '& .MuiTablePagination-select': { fontWeight: 600 },
        }}
      />
    </Box>
  );

  const renderOrderDetailsDialog = () => (
    <Dialog
      open={orderDetailsDialog}
      onClose={() => setOrderDetailsDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4, 
          maxHeight: '90vh',
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      {selectedOrder && (
        <>
          <DialogTitle sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
                  Order #{selectedOrder.id}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.grey[500], fontWeight: 500, mt: 0.25 }}>
                  {formatDate(selectedOrder.order_date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <OrderStatusBadge status={selectedOrder.status} />
                <IconButton 
                  onClick={() => setOrderDetailsDialog(false)}
                  sx={{ 
                    bgcolor: COLORS.grey[100],
                    '&:hover': { bgcolor: COLORS.grey[200] },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Order Summary */}
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <ReceiptIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Order Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Total Amount
                      </Typography>
                      <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.primary }}>
                        {formatCurrency(selectedOrder.total_amount || 0)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Payment Status
                      </Typography>
                      <Chip
                        label={getPaymentStatusLabel(selectedOrder.payment_status)}
                        size="small"
                        sx={{
                          bgcolor: alpha(getPaymentStatusColor(selectedOrder.payment_status), 0.1),
                          color: getPaymentStatusColor(selectedOrder.payment_status),
                          fontWeight: 700,
                          mt: 0.5,
                          border: `1px solid ${alpha(getPaymentStatusColor(selectedOrder.payment_status), 0.15)}`,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Delivery Method
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700], mt: 0.5, textTransform: 'capitalize' }}>
                        {selectedOrder.delivery_method || 'Standard'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Items
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700], mt: 0.5 }}>
                        {selectedOrder.items || 0} items
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Shipping Info */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <LocalShippingIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Shipping Information
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                    <strong>Address:</strong> {selectedOrder.shipping_address || 'Not specified'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700], mt: 0.5 }}>
                    <strong>Method:</strong> {selectedOrder.delivery_method || 'Standard'}
                  </Typography>
                  {selectedOrder.tracking && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700], mt: 0.5 }}>
                      <strong>Tracking:</strong> {selectedOrder.tracking}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Additional Info */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <ScheduleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Additional Information
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                    <strong>Order Date:</strong> {formatDate(selectedOrder.order_date)}
                  </Typography>
                  {selectedOrder.intended_use && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700], mt: 0.5 }}>
                      <strong>Intended Use:</strong> {selectedOrder.intended_use}
                    </Typography>
                  )}
                  {selectedOrder.notes && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700], mt: 0.5 }}>
                      <strong>Notes:</strong> {selectedOrder.notes}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Order Timeline */}
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <TimelineIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Order Timeline
                  </Typography>
                  <OrderTimeline order={selectedOrder} />
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2 }}>
            {(selectedOrder.status === 'pending' || selectedOrder.status === 'processing') && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setOrderDetailsDialog(false);
                  handleCancelOrder(selectedOrder);
                }}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Cancel Order
              </Button>
            )}
            <Button 
              onClick={() => setOrderDetailsDialog(false)}
              sx={{ 
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  const renderCancelDialog = () => (
    <Dialog
      open={cancelDialog}
      onClose={() => setCancelDialog(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4,
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CancelIcon sx={{ color: COLORS.error }} />
            <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
              Cancel Order
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setCancelDialog(false)}
            sx={{ 
              bgcolor: COLORS.grey[100],
              '&:hover': { bgcolor: COLORS.grey[200] },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Typography variant="body2" sx={{ color: COLORS.grey[600], fontWeight: 500, mb: 1 }}>
          Are you sure you want to cancel order <strong style={{ color: COLORS.primary }}>#{selectedOrder?.id}</strong>?
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.grey[500], fontWeight: 400, mb: 2 }}>
          This action cannot be undone. Please provide a reason for cancellation.
        </Typography>
        
        <TextField
          label="Cancellation Reason"
          placeholder="Why are you cancelling this order?"
          fullWidth
          multiline
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: COLORS.primary,
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button 
          onClick={() => setCancelDialog(false)}
          sx={{ 
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
          }}
        >
          Keep Order
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirmCancel}
          sx={{ 
            borderRadius: 2, 
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: `0 4px 16px ${alpha(COLORS.error, 0.3)}`,
            '&:hover': {
              boxShadow: `0 8px 24px ${alpha(COLORS.error, 0.4)}`,
            },
          }}
        >
          Yes, Cancel Order
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderChangePasswordDialog = () => (
    <Dialog
      open={changePasswordDialog}
      onClose={() => setChangePasswordDialog(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4,
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LockOutlinedIcon sx={{ color: COLORS.primary }} />
          <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
            Change Password
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <TextField 
            label="Current Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
          <TextField 
            label="New Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
          <TextField 
            label="Confirm New Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button 
          onClick={() => setChangePasswordDialog(false)}
          sx={{ 
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          sx={{ 
            bgcolor: COLORS.primary,
            fontWeight: 700,
            borderRadius: 2,
            px: 4,
            boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.3)}`,
            '&:hover': { 
              bgcolor: COLORS.primaryLight,
              boxShadow: `0 6px 24px ${alpha(COLORS.primary, 0.4)}`,
            },
          }}
        >
          Update Password
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  if (loading && orders.length === 0) {
    return <Preloader text="Loading Orders..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
     <Sidebar
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  isMobile={isMobile}
  selectedTab={selectedTab}
  onTabChange={handleTabChange}  // ✅ This is correctly added
  userData={userData}
  wishlistCount={0}
  cartItemCount={0}
  onLogout={handleLogout}
/>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={handleChangePassword}
        
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {/* Header */}
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography 
                  variant="h4" 
                  fontWeight={800} 
                  sx={{ 
                    color: COLORS.grey[900],
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                  }}
                >
                  <ShoppingBagIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                  My Requests
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.grey[500], fontWeight: 500, mt: 0.25 }}>
                  {filteredOrders.length} Request found • Last updated: {lastRefreshed.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: COLORS.grey[300],
                    color: COLORS.grey[700],
                    '&:hover': {
                      borderColor: COLORS.primary,
                      color: COLORS.primary,
                      bgcolor: alpha(COLORS.primary, 0.04),
                    },
                  }}
                >
                  Refresh
                </Button>
              
              </Box>
            </Box>
          </Box>

          {/* Stats */}
          {renderStats()}

          {/* Filters */}
          {renderFilters()}

          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={viewMode === 'table' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('table')}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 700,
                  ...(viewMode === 'table' && {
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                    boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.25)}`,
                  }),
                }}
              >
                Table View
              </Button>
              <Button
                size="small"
                variant={viewMode === 'card' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('card')}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 700,
                  ...(viewMode === 'card' && {
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                    boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.25)}`,
                  }),
                }}
              >
                Card View
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600 }}>
              Showing {Math.min(filteredOrders.length, rowsPerPage)} of {filteredOrders.length} orders
            </Typography>
          </Box>

          {/* Orders List */}
          {viewMode === 'table' ? renderTable() : renderCardView()}

          {/* Dialogs */}
          {renderOrderDetailsDialog()}
          {renderCancelDialog()}
          {renderChangePasswordDialog()}
        </Box>

        <Footer />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
            fontWeight: 600,
            '& .MuiAlert-icon': { 
              alignItems: 'center',
              fontSize: 24,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global styles */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          ::-webkit-scrollbar-track {
            background: ${COLORS.grey[100]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: ${COLORS.grey[300]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: ${COLORS.grey[400]};
          }
          @media print {
            .MuiPaper-root {
              box-shadow: none !important;
              border: 1px solid ${COLORS.grey[300]} !important;
            }
            .MuiButton-root {
              display: none !important;
            }
            .MuiIconButton-root {
              display: none !important;
            }
            .MuiTablePagination-root {
              display: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default BuyerOrderManagement;